import * as React from 'react';

export type UseMeasureRect = Pick<
  DOMRectReadOnly,
  'x' | 'y' | 'top' | 'left' | 'right' | 'bottom' | 'height' | 'width'
>;

export type UseMeasureRef<E extends Element = Element> = (
  element: E | null
) => void;

export type UseMeasureResult<E extends Element = Element> = [
  /* ref отслеживаемого элемента */
  UseMeasureRef<E>,
  /* размеры отслеживаемого элемента */
  UseMeasureRect,
  /* сам отслеживаемый элемент */
  E | null
];

const defaultState: UseMeasureRect = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
};

/**
 * Хук возвращает bounds у HTML-элемента
 */
const useMeasure = <E extends Element = Element>(): UseMeasureResult<E> => {
  const [element, ref] = React.useState<E | null>(null);

  const [rect, setRect] = React.useState<UseMeasureRect>(defaultState);

  const observer = React.useMemo(
    () =>
      new ResizeObserver((entries) => {
        if (entries[0]) {
          const { x, y, width, height, top, left, bottom, right } =
            entries[0].contentRect;

          setRect({ x, y, width, height, top, left, bottom, right });
        }
      }),
    []
  );

  React.useEffect(() => {
    if (!element) {
      return;
    }

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [element, observer]);

  return [ref, rect, element];
};

export default useMeasure;
